// -----------------------------------------------------------------------------
// <infinite-scroll>
// -----------------------------------------------------------------------------
// Progressive-enhancement custom element that appends subsequent paginated
// pages into a target container as the user scrolls, using Shopify's Section
// Rendering API (?section_id=X) so only the section HTML is fetched.
//
// It plays nicely with the theme's existing facet:update handler in theme.js:
// when a facet or sort changes, the parent shopify-section is replaced wholesale
// and this element is torn down with it, so a fresh instance mounts on page 1
// with the new filter state. No manual reset logic required here.
//
// Attributes:
//   section-id        Shopify section id (required)
//   target-selector   Selector for the element that should receive appended
//                     children, queried within the section (default: .product-list)
//   next-url          Fully qualified URL for the next page; when absent the
//                     element stays idle and the numeric pagination nav remains
//                     visible as a fallback
// -----------------------------------------------------------------------------

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this._loading = false;
    this._abortController = null;
    this._onIntersect = this._onIntersect.bind(this);
  }

  connectedCallback() {
    if (!('IntersectionObserver' in window) || !('fetch' in window)) {
      return;
    }

    // Hide the numeric pagination nav once JS is confirmed to be running. The
    // nav stays in the DOM for accessibility and as a no-JS fallback, but is
    // visually hidden while infinite scroll is driving the experience.
    this._paginationNav = this.querySelector('nav.pagination');
    if (this._paginationNav) {
      this._paginationNav.setAttribute('hidden', '');
    }

    this._ensureLoader();

    if (!this.getAttribute('next-url')) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersect, {
      rootMargin: '600px 0px',
    });
    this._observer.observe(this);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  _ensureLoader() {
    if (this._loader) return;
    this._loader = document.createElement('div');
    this._loader.className = 'infinite-scroll__loader text-center text-subdued text-sm';
    this._loader.style.padding = '1rem 0';
    this._loader.setAttribute('role', 'status');
    this._loader.setAttribute('aria-live', 'polite');
    this._loader.hidden = true;
    this._loader.textContent = 'Loading…';
    this.appendChild(this._loader);
  }

  _setLoading(state) {
    this._loading = state;
    if (this._loader) this._loader.hidden = !state;
  }

  _onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting && !this._loading) {
        this._loadNext();
      }
    }
  }

  async _loadNext() {
    const nextUrl = this.getAttribute('next-url');
    if (!nextUrl) return;

    const sectionId = this.getAttribute('section-id');
    if (!sectionId) return;

    const targetSelector = this.getAttribute('target-selector') || '.product-list';
    const sectionWrapper = document.getElementById(`shopify-section-${sectionId}`);
    const target = sectionWrapper ? sectionWrapper.querySelector(targetSelector) : null;
    if (!target) return;

    this._setLoading(true);
    this._abortController = new AbortController();

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), { signal: this._abortController.signal });
      if (!response.ok) {
        throw new Error(`Infinite scroll fetch failed: HTTP ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedTarget = doc.querySelector(targetSelector);
      const fetchedSelf = doc.querySelector('infinite-scroll');

      if (fetchedTarget) {
        const fragment = document.createDocumentFragment();
        Array.from(fetchedTarget.children).forEach((child) => {
          fragment.appendChild(document.importNode(child, true));
        });
        target.appendChild(fragment);

        this.dispatchEvent(new CustomEvent('infinite-scroll:appended', {
          bubbles: true,
          detail: { target, appendedFrom: url.toString() },
        }));
      }

      const newNextUrl = fetchedSelf ? fetchedSelf.getAttribute('next-url') : null;
      if (newNextUrl) {
        this.setAttribute('next-url', newNextUrl);
      } else {
        this.removeAttribute('next-url');
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('[infinite-scroll]', err);
      }
    } finally {
      this._setLoading(false);
      this._abortController = null;
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
